import type { FormData } from '@components/pages/FormBuilder/PendingFormsTable/CompletedFormsModal.component'
import type {
  AppointmentDetail,
  IncompleteFormEntry,
  PendingFormEntry,
} from '@pages/FormBuilder/assignedForms.types'

export interface SendingAppointmentDetailInput {
  appointmentId: string
  appointmentType: string
  roomNo: string
  appointmentDate: string
  lastReminded: string
}

export interface CreateAssignedFormEntryParams {
  patientId: string
  publicPatientId?: string
  patientName: string
  /** All appointments for the patient (from Send Forms row). */
  appointments: SendingAppointmentDetailInput[]
  /** Appointment IDs that were selected in Assign Forms step 1. */
  selectedScheduleIds: string[]
  /** Forms selected in Assign Forms (template converted to FormData). */
  selectedForms: FormData[]
  assignmentIds?: string[]
}

const DEFAULT_LAST_REMINDED = ''
const INITIAL_FORMS_STATUS = 'pending'

/**
 * Converts Assign Forms modal selections into a single PendingFormEntry.
 * Used when user confirms "Send Forms" so the Pending tab can show the new assignment.
 * Defaults: completedFormsCount = 0, completedForms = [], incompleteForms = selected forms.
 */
export function createAssignedFormEntry(
  params: CreateAssignedFormEntryParams,
): PendingFormEntry {
  const {
    patientId,
    publicPatientId,
    patientName,
    appointments,
    selectedScheduleIds,
    selectedForms,
    assignmentIds,
  } = params

  const incompleteForms: IncompleteFormEntry[] = selectedForms.map(
    (f, index) => ({
      id: f.id,
      name: f.name,
      assignmentId: assignmentIds?.[index],
    }),
  )
  const totalFormsCount = selectedForms.length

  const appointmentDetails: AppointmentDetail[] =
    selectedScheduleIds.length === 0
      ? [
          {
            appointmentId: '',
            source: 'direct',
            appointmentType: 'Direct request',
            roomNo: '—',
            appointmentDate: '',
            lastReminded: '',
            formsStatus: INITIAL_FORMS_STATUS,
            completedFormsCount: 0,
            totalFormsCount,
            completedForms: [],
            incompleteForms,
          },
        ]
      : appointments
          .filter((apt) => selectedScheduleIds.includes(apt.appointmentId))
          .map((apt) => ({
            appointmentId: apt.appointmentId,
            source: 'appointment',
            appointmentType: apt.appointmentType,
            roomNo: apt.roomNo,
            appointmentDate: apt.appointmentDate,
            lastReminded: apt.lastReminded || DEFAULT_LAST_REMINDED,
            formsStatus: INITIAL_FORMS_STATUS,
            completedFormsCount: 0,
            totalFormsCount,
            completedForms: [],
            incompleteForms,
          }))

  return {
    patientId,
    ...(publicPatientId !== undefined && { publicPatientId }),
    patientName,
    numberOfAppointments: appointmentDetails.filter((apt) => apt.appointmentId)
      .length,
    appointments: appointmentDetails,
  }
}

/**
 * Merges a newly assigned form entry into the existing pending list.
 * - If no row for patientId, appends the new entry.
 * - If row exists, merges appointments: same appointmentId gets incomplete forms appended (no duplicate form ids).
 * - New appointments are added to the row.
 */
export function mergeAssignedFormEntry(
  existing: PendingFormEntry[],
  newEntry: PendingFormEntry,
): PendingFormEntry[] {
  const idx = existing.findIndex((e) => e.patientId === newEntry.patientId)
  if (idx === -1) {
    return [...existing, newEntry]
  }

  const row = existing[idx]!
  const existingAppointmentIds = new Set(
    row.appointments.map((a) => a.appointmentId),
  )
  const mergedAppointments: AppointmentDetail[] = [...row.appointments]

  for (const newApt of newEntry.appointments) {
    const existingAptIdx = row.appointments.findIndex(
      (a) => a.appointmentId === newApt.appointmentId,
    )
    if (existingAptIdx === -1) {
      mergedAppointments.push(newApt)
      existingAppointmentIds.add(newApt.appointmentId)
      continue
    }

    const existingApt = mergedAppointments[existingAptIdx]!
    const existingFormIds = new Set<string>()
    for (const f of existingApt.completedForms ?? []) {
      existingFormIds.add(f.assignmentId ?? f.id)
    }
    for (const f of existingApt.incompleteForms ?? []) {
      existingFormIds.add(f.assignmentId ?? f.id)
    }
    const currentIncomplete = [...(existingApt.incompleteForms ?? [])]
    for (const f of newApt.incompleteForms ?? []) {
      if (!existingFormIds.has(f.assignmentId ?? f.id)) {
        currentIncomplete.push(f)
        existingFormIds.add(f.assignmentId ?? f.id)
      }
    }
    const newTotal = existingFormIds.size
    mergedAppointments[existingAptIdx] = {
      ...existingApt,
      incompleteForms: currentIncomplete,
      formsStatus:
        currentIncomplete.length > 0
          ? INITIAL_FORMS_STATUS
          : existingApt.formsStatus,
      totalFormsCount: newTotal,
      completedFormsCount: existingApt.completedFormsCount ?? 0,
    }
  }

  const mergedRow: PendingFormEntry = {
    ...row,
    publicPatientId: newEntry.publicPatientId ?? row.publicPatientId,
    numberOfAppointments: mergedAppointments.filter((apt) => apt.appointmentId)
      .length,
    appointments: mergedAppointments,
  }

  const next = [...existing]
  next[idx] = mergedRow
  return next
}

const appointmentHasPendingForms = (apt: AppointmentDetail): boolean => {
  if ((apt.incompleteForms?.length ?? 0) > 0) return true
  const total = apt.totalFormsCount ?? 0
  const completed = apt.completedFormsCount ?? 0
  return total > 0 && completed < total
}

const appointmentFormIds = (apt: AppointmentDetail): Set<string> => {
  const ids = new Set<string>()
  for (const form of apt.completedForms ?? []) {
    ids.add(form.assignmentId ?? form.id)
  }
  for (const form of apt.incompleteForms ?? []) {
    ids.add(form.assignmentId ?? form.id)
  }
  return ids
}

const appointmentHasLocalFormsMissingFromApi = (
  localApt: AppointmentDetail,
  apiApt: AppointmentDetail | undefined,
): boolean => {
  if (!apiApt) return true
  const apiFormIds = appointmentFormIds(apiApt)
  return (localApt.incompleteForms ?? []).some(
    (form) => !apiFormIds.has(form.assignmentId ?? form.id),
  )
}

/**
 * Prefer the API list, but keep locally assigned pending rows/appointments
 * that the pending endpoint has not returned yet (common right after send).
 */
export function reconcilePendingEntries(
  fromApi: PendingFormEntry[],
  local: PendingFormEntry[],
): PendingFormEntry[] {
  let next = fromApi

  for (const localEntry of local) {
    const apiEntry = fromApi.find(
      (entry) => entry.patientId === localEntry.patientId,
    )
    if (!apiEntry) {
      next = mergeAssignedFormEntry(next, localEntry)
      continue
    }

    const missingOrStaleAppointments = localEntry.appointments.filter((apt) => {
      if (!appointmentHasPendingForms(apt)) return false
      const apiApt = apiEntry.appointments.find(
        (candidate) => candidate.appointmentId === apt.appointmentId,
      )
      return appointmentHasLocalFormsMissingFromApi(apt, apiApt)
    })

    if (missingOrStaleAppointments.length === 0) continue

    next = mergeAssignedFormEntry(next, {
      ...localEntry,
      appointments: missingOrStaleAppointments,
      numberOfAppointments: missingOrStaleAppointments.length,
    })
  }

  return next
}
